/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Tabs, Tab, Form } from "react-bootstrap";
import { QrReader } from "react-qr-reader";
import jsQR from "jsqr";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { BsArrowLeftCircle } from "react-icons/bs";
import axios from 'axios';

const PatientLoginPage = () => {
    // États
    const [selectedLanguage, setSelectedLanguage] = useState("fr");
    const [step, setStep] = useState("select"); // État initial modifié à "select"
    const [phone, setPhone] = useState("");
    const [formData, setFormData] = useState({ firstname: "", lastname: "", birthdate: "", qrCode: "" });
    const [showModalSuccess, setShowModalSuccess] = useState(false);
    const [scanTabResult, setScanTabResult] = useState("");
    const [manualTabResult, setManualTabResult] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [facingMode, setFacingMode] = useState("environment");
    const [scannerKey, setScannerKey] = useState(Date.now());
    const [isProcessing, setIsProcessing] = useState(false);
    const [authError, setAuthError] = useState("");
    const [authSuccess, setAuthSuccess] = useState(false);
    const [phoneError, setPhoneError] = useState("");
    const [otherinfoModal, setotherinfoModal] = useState(false);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // Fonction pour gérer l'extraction du QR Code via l'upload d'image
    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setIsProcessing(true);
            setAuthError("");

            const reader = new FileReader();
            reader.onload = async (e) => {
                const img = new Image();
                img.src = e.target.result;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, img.width, img.height);

                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

                    if (qrCode) {
                        setManualTabResult(qrCode.data);
                        setFormData({ ...formData, qrCode: qrCode.data });
                        setIsProcessing(false);
                        setStep("personal-info");
                    } else {
                        setAuthError("QR Code non détecté dans l'image.");
                        setIsProcessing(false);
                    }
                };
            };
            reader.readAsDataURL(file);
            setSelectedImage(file);
        }
    };

    // Fonction pour arrêter tous les flux vidéo actifs
    const stopAllVideoStreams = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
    };

    // Initialiser ou arrêter la caméra selon l'étape actuelle
    useEffect(() => {
        if (step === "existing") {
            // Only initialize camera if it's not already running
            if (!videoRef.current || !videoRef.current.srcObject) {
                navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: facingMode,
                        width: { ideal: 720 },
                        height: { ideal: 480 }
                    }
                })
                    .then(stream => {
                        if (videoRef.current) {
                            videoRef.current.srcObject = stream;
                        }
                    })
                    .catch(err => {
                        console.error("Erreur d'accès à la caméra :", err);
                        setAuthError("Impossible d'accéder à la caméra. Veuillez vérifier les permissions.");
                    });
            }
        } else {
            // Stop camera when leaving the step
            stopAllVideoStreams();
        }

        return () => {
            // Cleanup function to stop the stream when component unmounts
            stopAllVideoStreams();
        };
    }, [step]); 

    // Capturer une photo avec la caméra
    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            setIsProcessing(true);
            setAuthError("");

            const video = videoRef.current;
            const canvas = canvasRef.current;

            // Définir les dimensions du canvas pour correspondre à la vidéo
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Dessiner l'image de la vidéo sur le canvas
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Tenter de détecter un QR code dans l'image
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

            if (qrCode) {
                setManualTabResult(qrCode.data);
                setFormData({ ...formData, qrCode: qrCode.data });
                setIsProcessing(false);
                setStep("personal-info");
            } else {
                setAuthError("QR Code non détecté. Veuillez réessayer.");
                setIsProcessing(false);
            }
        }
    };

    // Fonction pour changer de caméra
    const toggleCamera = () => {
        // First stop the current stream
        stopAllVideoStreams();

        // Update the facing mode state
        const newFacingMode = facingMode === "environment" ? "user" : "environment";
        setFacingMode(newFacingMode);

        // Create new stream with updated facing mode
        navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: newFacingMode,
                width: { ideal: 720 },
                height: { ideal: 480 }
            }
        })
            .then(stream => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            })
            .catch(err => {
                console.error("Erreur d'accès à la caméra :", err);
                setAuthError("Impossible d'accéder à la caméra. Veuillez vérifier les permissions.");
            });
    };

    // Authentification d'un patient existant
    const handleExistingPatient = async (e) => {
        e.preventDefault();
        // Vérifier que tous les champs requis sont remplis
        if (!formData.qrCode) {
            setAuthError("Veuillez scanner un QR code ou télécharger une image contenant un QR code.");
            return;
        }

        if (!formData.birthdate || !formData.phone) {
            setAuthError("Veuillez compléter tous les champs obligatoires.");
            return;
        }

        setIsProcessing(true);
        setAuthError("");
        setAuthSuccess(false);

        try {
            // const response = await axios.post("https://kali.medtinz.com/clinic/authenticatePatient", formData);
            const response = await axios.post("http://localhost:5000/clinic/authenticatePatient", formData);

            setAuthSuccess(true);
            setIsProcessing(false);
            const accessToken = response.data.token;
            if (accessToken) {
                localStorage.setItem("access_token",accessToken)
                // window.location.href =  `https://medtinz.com/hospitaladmin/Appointmentlist?token=${accessToken}&showModal=true`;
                window.location.href = `http://localhost:3000/hospitaladmin/Appointmentlist?token=${accessToken}&showModal=true`;
            } else {
                alert("Informations incorrectes ou erreur de connexion");
                throw new Error('Access token manquant dans la réponse');
            }

        } catch (error) {
            setIsProcessing(false);
            alert("Informations incorrectes ou erreur de connexion");
            setAuthError(error.response ? error.response.data.message : "Erreur de connexion au serveur");
        }
    };

    // Enregistrement d'un nouveau patient
    const handleNewPatient = async (e) => {
        e.preventDefault();

        if (!phone) {
            alert('Phone number is required');
            return;
        }

        const patientData = {
            lastName: e.target.elements.lastName.value,
            firstName: e.target.elements.firstName.value,
            birthDate: e.target.elements.birthDate.value,
            phoneNumber: phone,
        };
        setPhoneError(""); // Clear error if valid

        // Ici, vous pouvez ajouter la logique d'API pour créer un nouveau patient
        try {
            // Simuler une réponse d'API réussie
            // const response = await axios.post("http://localhost:5000/clinic/addPatient", patientData);
            // console.log(response);
            
            // Pour l'exemple, je simule une réponse réussie sans appel d'API
            setTimeout(() => {
                setStep("select");
                setShowModalSuccess(true); // Affiche le modal de succès
            }, 1000);
        } catch (error) {
            console.error(error);
            alert('Une erreur s\'est produite');
        }
    };

    // Fermeture du modal
    const closeModal = () => {
        // Réinitialiser les états
        setStep("select");
        setCameraActive(false);
        setScanTabResult("");
        setManualTabResult("");
        setSelectedImage(null);
        setFormData({ firstname: "", lastname: "", phone: "", birthdate: "", qrCode: "" });
        setAuthError("");
        setAuthSuccess(false);
        
        // Rediriger ou faire autre chose quand le modal est fermé
        window.location.href = "/"; // ou toute autre action de fermeture
    };

    const handleCloseModal = (e) => {
        e.preventDefault();
        closeModal();
    };

    // Fonction pour simuler le chargement de contenu basé sur la langue
    const rendershortContent = (contentKey) => {
        // Simuler un contenu basé sur la clé fournie
        const contentMap = {
            "contents?.data.book_choose": "Choisissez une option ci-dessous",
            "contents?.data.book_old": "Patient existant",
            "contents?.data.book_fill": "Veuillez compléter les informations suivantes",
            "contents?.data.book_back": "Retour",
            "contents?.data.book_qr_detected": "QR code détecté avec succès"
        };
        
        return <span>{contentMap[contentKey] || contentKey}</span>;
    };

    return (
        <div>
            <div className="choosestagepatientbtn modal fade show d-block" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body position-relative">
                            <div className="d-flex justify-content-center">
                                <div className="col">
                                    <div className="color" style={{ padding: '60px' }}>
                                        <div className="mb-6">
                                            <h1 className="text-2xl font-bold text-blue-900 text-center">
                                                Patient portal
                                            </h1>
                                            <p className='text-center'>{rendershortContent("contents?.data.book_choose")}</p>
                                            <br></br>
                                            <a
                                                href="#"
                                                className="goback"
                                                onClick={handleCloseModal}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        textDecoration: 'underline',
                                                    }}
                                                >
                                                    <BsArrowLeftCircle style={{ marginRight: '8px' }} />
                                                   back
                                                </span>
                                            </a>
                                        </div>

                                        <div>
                                            {step === "select" && (
                                                <div className="d-grid gap-2">
                                                    <Button className="newbtn" onClick={() => setStep("new")}>
                                                       new patient
                                                    </Button>
                                                    <Button className="existbtn" onClick={() => setStep("existing")}>
                                                    existing patient
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Modal d'authentification */}
            {step === "existing" && (
                <div className="choosestagepatientbtn modal fade show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body position-relative">
                                <div className="d-flex justify-content-center">
                                    <div className="col">
                                        <div className="color" style={{ padding: '60px' }}>
                                            <div className="mb-6">
                                                <h1 className="text-2xl font-bold text-blue-900 text-center">
                                                    Patient existant
                                                </h1>
                                                <p className='text-center'>
                                                   Veuillez scanner votre QR code
                                                </p>
                                                <br></br>
                                                <br></br>
                                                {/* Bouton Back */}
                                                <a href="#" className="goback" onClick={(e) => {
                                                    e.preventDefault();
                                                    setStep("select");
                                                }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'underline' }}>
                                                        <BsArrowLeftCircle style={{ marginRight: '8px' }} />
                                                        Retour
                                                    </span>
                                                </a>
                                            </div>

                                            {authError && (
                                                <div className="alert alert-danger">{authError}</div>
                                            )}

                                            <div>
                                                <form className="space-y-8">
                                                    <div className="space-y-2">
                                                        {/* Affichage de la section pour la caméra */}
                                                        <div className="mb-4">
                                                            <div className="mb-3">
                                                                <label className="form-label">Scannez votre QR code</label>
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <div className="position-relative" style={{
                                                                        width: "100%", height: "auto",
                                                                        border: "2px solid #13AB9C",
                                                                        backgroundColor: '#D8D8D838'
                                                                    }}>
                                                                        <video
                                                                            ref={videoRef}
                                                                            style={{
                                                                                width: "100%", height: "auto",
                                                                                borderRadius: "4px",
                                                                            }}
                                                                            autoPlay
                                                                            playsInline
                                                                        ></video>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-light position-absolute top-0 end-0 m-2"
                                                                            onClick={toggleCamera}
                                                                        >
                                                                            <i className="bi bi-camera-switch"></i>
                                                                            Changer de caméra
                                                                        </button>
                                                                    </div>
                                                                    <Button
                                                                        variant="primary"
                                                                        onClick={capturePhoto}
                                                                        className="mb-3 mt-3"
                                                                    >
                                                                        Capturer photo
                                                                    </Button>
                                                                    <canvas
                                                                        ref={canvasRef}
                                                                        style={{ display: "none" }}
                                                                    ></canvas>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <p className="divider-text text-center">
                                                        <span>&nbsp;&nbsp; ou &nbsp;&nbsp;</span>
                                                    </p>

                                                    <div className="space-y-2 existingpatientfile">
                                                        <div className="custom-file-input">
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={handleImageUpload}
                                                                id="file-upload"
                                                            />
                                                            <label htmlFor="file-upload" className="file-label">Télécharger une image</label>
                                                        </div>

                                                        {selectedImage && (
                                                            <p className="text-muted mt-1">
                                                               Image sélectionnée : {selectedImage.name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Formulaire nouveau patient */}
            {step === "new" && (
                <div className="choosestagepatientbtn modal fade show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body position-relative">
                                <div className="d-flex justify-content-center">
                                    <div className="col">
                                        <div className="color" style={{ padding: '60px' }}>
                                            <div className="mb-6">
                                                <h1 className="text-2xl font-bold text-blue-900 text-center">
                                                    Nouveau patient
                                                </h1>
                                                <p className='text-center'>Veuillez compléter les informations suivantes</p>
                                                <br></br>
                                                <br></br>
                                                <a href="#" className="goback" onClick={(e) => {
                                                    e.preventDefault();
                                                    setStep("select");
                                                }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'underline' }}>
                                                        <BsArrowLeftCircle style={{ marginRight: '8px' }} />
                                                        Retour
                                                    </span>
                                                </a>
                                            </div>

                                            <div>
                                                <form className="space-y-8" onSubmit={handleNewPatient}>
                                                    <div className="space-y-2">
                                                        <label className="block text-blue-900">
                                                            Nom <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="lastName"
                                                            required
                                                            placeholder="Votre nom"
                                                            className="form-control"
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="block text-blue-900">
                                                            Prénom <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="firstName"
                                                            required
                                                            placeholder="Votre prénom"
                                                            className="form-control"
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="block text-blue-900">
                                                            Date de naissance<span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            max={new Date().toISOString().split("T")[0]}
                                                            type="date"
                                                            name="birthDate"
                                                            required
                                                            placeholder="Date de naissance"
                                                            className="form-control"
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="block text-blue-900">
                                                            Téléphone<span className="text-red-500">*</span>
                                                        </label>
                                                        <PhoneInput
                                                            country={"bj"}
                                                            value={phone}
                                                            onChange={setPhone}
                                                            inputStyle={{
                                                                width: "100%",
                                                                border: "none",
                                                                boxShadow: "none",
                                                                paddingLeft: "50px"
                                                            }}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="d-flex justify-content-center">
                                                        <button type="submit" className="btn btn-primary" style={{ padding: '15px 40px' }}>
                                                            Soumettre
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de succès */}
            {showModalSuccess && (
                <div className="choosestagepatientbtn modal fade show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body position-relative">
                                <div className="d-flex justify-content-center">
                                    <div className="col">
                                        <div className="color">
                                            <div className="mb-6 text-center">
                                                <h1 className="text-success">Succès</h1>
                                                <p className="text-success">Votre compte a été créé avec succès!</p>
                                                <br></br>
                                                <button
                                                    className="btn btn-success"
                                                    onClick={() => setShowModalSuccess(false)}
                                                    style={{ padding: '10px 20px' }}
                                                >
                                                    Fermer
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Formulaire informations personnelles */}
            {step === "personal-info" && (
                <div className="choosestagepatientbtn modal fade show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body position-relative">
                                <div className="d-flex justify-content-center">
                                    <div className="col">
                                        <div className="color" style={{ padding: '60px' }}>
                                            <div className="mb-6">
                                                <h1 className="text-2xl font-bold text-blue-900 text-center">
                                                    {rendershortContent("contents?.data.book_old")}
                                                </h1>
                                                <p className='text-center'>
                                                {rendershortContent("contents?.data.book_fill")}
                                                </p>
                                                <br></br>
                                                <br></br>
                                                <a href="#" className="goback" onClick={(e) => {
                                                    e.preventDefault();
                                                    setotherinfoModal(false);
                                                    setStep("existing");
                                                }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'underline' }}>
                                                        <BsArrowLeftCircle style={{ marginRight: '8px' }} />
                                                        {rendershortContent("contents?.data.book_back")}
                                                    </span>
                                                </a>
                                            </div>

                                            {manualTabResult && (
                                                <div className="alert alert-success mt-2">
                                                    {rendershortContent("contents?.data.book_qr_detected")}
                                                </div>
                                            )}

                                            <div>
                                                <form className="space-y-8" onSubmit={handleExistingPatient}>
                                                    <div className="space-y-2">
                                                        <label className="block text-blue-900">
                                                            Date de naissance<span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            max={new Date().toISOString().split("T")[0]}
                                                            type="date"
                                                            name="birthdate"
                                                            value={formData.birthdate}
                                                            onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                                                            required
                                                            placeholder="Date de naissance"
                                                            className="form-control"
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="block text-blue-900">
                                                            Téléphone<span className="text-red-500">*</span>
                                                        </label>
                                                        <PhoneInput
                                                            country="bj"
                                                            value={formData.phone}
                                                            onChange={(phone) => setFormData({ ...formData, phone })}
                                                            inputStyle={{ width: "100%" }}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="d-flex justify-content-center">
                                                        <button type="submit" className="btn btn-primary" style={{ padding: '15px 40px' }}>
                                                            Soumettre
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientLoginPage;